import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import s from './DatabasesList.module.css';
import { useDatabases, useDeleteDatabase, usePauseDatabase, useRunBackupNow, } from '@/api/useDatabases';
import { Button } from '@/components/primitives/Button';
import { Input } from '@/components/primitives/Input';
import { Icon } from '@/components/Icon';
import { StatusPill } from '@/components/primitives/StatusPill';
import { StatusDot } from '@/components/primitives/StatusDot';
import { Spinner } from '@/components/primitives/Spinner';
import { Kebab } from '@/components/primitives/Kebab';
import { Card } from '@/components/primitives/Card';
import { StorageBadges } from '@/components/StorageBadges';
import { relativeTime } from '@/lib/relativeTime';
import { formatBytes } from '@/lib/bytes';
import { cronToPreset } from '@/lib/cron';
import { DatabaseFormSheet } from './DatabaseFormSheet';
function tone(status) {
    if (status === 'ok')
        return { dot: 'ok', pill: 'ok' };
    if (status === 'partial')
        return { dot: 'warn', pill: 'warn' };
    if (status === 'failed')
        return { dot: 'fail', pill: 'fail' };
    if (status === 'running')
        return { dot: 'info', pill: 'running' };
    return { dot: 'paused', pill: 'paused' };
}
export function DatabasesList() {
    const list = useDatabases();
    const del = useDeleteDatabase();
    const pause = usePauseDatabase();
    const runNow = useRunBackupNow();
    const [filter, setFilter] = useState('all');
    const [q, setQ] = useState('');
    const [editId, setEditId] = useState(null);
    const [adding, setAdding] = useState(false);
    const rows = useMemo(() => {
        const items = list.data ?? [];
        return items.filter((r) => {
            if (q && !`${r.name} ${r.host} ${r.dbname}`.toLowerCase().includes(q.toLowerCase()))
                return false;
            if (filter === 'healthy' && r.last_backup_status !== 'ok')
                return false;
            if (filter === 'issues' && r.last_backup_status !== 'partial' && r.last_backup_status !== 'failed')
                return false;
            if (filter === 'paused' && r.enabled)
                return false;
            return true;
        });
    }, [list.data, q, filter]);
    const total = list.data?.length ?? 0;
    const healthy = list.data?.filter((r) => r.last_backup_status === 'ok').length ?? 0;
    const issues = list.data?.filter((r) => r.last_backup_status === 'partial' || r.last_backup_status === 'failed')
        .length ?? 0;
    const paused = list.data?.filter((r) => !r.enabled).length ?? 0;
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: s.header, children: [_jsxs("div", { children: [_jsx("h1", { className: s.h1, children: "Databases" }), _jsxs("div", { className: s.h1Sub, children: [total, " sources \u00B7 ", healthy, " healthy \u00B7 ", issues, " with issues \u00B7 ", paused, " paused"] })] }), _jsxs(Button, { onClick: () => setAdding(true), children: [_jsx(Icon, { name: "plus", size: 14 }), " Add database"] })] }), _jsxs("div", { className: s.toolbar, children: [_jsxs("div", { className: s.search, children: [_jsx(Icon, { name: "search", size: 14 }), _jsx(Input, { value: q, onChange: (e) => setQ(e.target.value), placeholder: "Search by name, host, or dbname" })] }), _jsx("div", { className: s.tabs, children: ['all', 'healthy', 'issues', 'paused'].map((k) => (_jsxs("button", { type: "button", className: `${s.tab} ${filter === k ? s.tabActive : ''}`, onClick: () => setFilter(k), children: [_jsx("span", { style: { textTransform: 'capitalize' }, children: k }), _jsx("span", { className: s.tabCount, children: k === 'all' ? total : k === 'healthy' ? healthy : k === 'issues' ? issues : paused })] }, k))) })] }), _jsxs(Card, { className: s.list, children: [_jsxs("div", { className: s.rowHead, children: [_jsx("div", {}), _jsx("div", { children: "Database" }), _jsx("div", { children: "Connection" }), _jsx("div", { children: "Schedule" }), _jsx("div", { children: "Storages" }), _jsx("div", { children: "Last backup" }), _jsx("div", {})] }), rows.map((r) => {
                        const t = tone(r.last_backup_status);
                        const isRunning = r.last_backup_status === 'running';
                        return (_jsxs("div", { className: s.row, children: [isRunning ? _jsx(Spinner, { size: 12 }) : _jsx(StatusDot, { tone: t.dot }), _jsxs(Link, { to: `/databases/${r.id}`, className: s.nameCell, children: [_jsx("div", { className: s.name, children: r.name }), _jsxs("div", { className: s.meta2, children: [formatBytes(r.size_bytes ?? 0), " \u00B7 ", r.backups_stored ?? 0, " stored"] })] }), _jsxs("div", { className: s.meta, children: [r.host, ":", r.port, " / ", r.dbname] }), _jsx("div", { children: (() => {
                                        const p = cronToPreset(r.schedule_cron);
                                        return (_jsx("span", { className: s.chip, title: r.schedule_cron, children: p === 'custom' ? r.schedule_cron : p }));
                                    })() }), _jsx(StorageBadges, { items: r.storages ?? [] }), _jsxs("div", { className: s.last, children: [_jsx(StatusPill, { tone: t.pill, children: r.last_backup_status ?? '—' }), r.last_backup_at && (_jsx("span", { className: s.time, children: relativeTime(r.last_backup_at) }))] }), _jsx(Kebab, { items: [
                                        { label: 'Run backup now', icon: 'play', onClick: () => runNow.mutate(r.id) },
                                        {
                                            label: r.enabled ? 'Pause' : 'Resume',
                                            icon: 'pause',
                                            onClick: () => pause.mutate({ id: r.id, paused: r.enabled }),
                                        },
                                        { label: 'Edit', icon: 'edit', onClick: () => setEditId(r.id) },
                                        {
                                            label: 'Delete',
                                            icon: 'trash',
                                            danger: true,
                                            onClick: () => {
                                                if (confirm(`Delete ${r.name}? Backups must be purged first.`))
                                                    del.mutate(r.id);
                                            },
                                        },
                                    ] })] }, r.id));
                    }), _jsxs("div", { className: s.footer, children: ["Showing ", rows.length, " of ", total, " databases"] })] }), _jsx(AnimatePresence, { children: (adding || editId) && (_jsx(DatabaseFormSheet, { mode: adding ? 'create' : 'edit', databaseId: editId ?? undefined, onClose: () => {
                        setAdding(false);
                        setEditId(null);
                    } })) })] }));
}
